import { FilterAltOff } from '@mui/icons-material'
import { Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useTranslate } from '@tolgee/react'
import { PlusIcon, useMinimalsTheme } from '@tom-ui/ui'
import { CarrierVisitDirection } from '@yard/app/api'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import { YARD_DRAWER_HEADER_HEIGHT, YARD_DRAWERS_WIDTH } from '../../helpers/yard-view.utils'
import { useUnsavedChangesGuard } from '../../hooks/use-unsaved-changes-guard'
import { CarrierVisitAllocationRulesViewStore } from '../../stores/allocation-rules/carrier-visit-allocation-rules.view-store'
import { GlobalAllocationRulesViewStore } from '../../stores/allocation-rules/global-allocation-rules.view-store'
import { YardViewStore } from '../../stores/yard.view-store'
import { UnsavedChangesDialog } from '../molecules/UnsavedChangesDialog'
import { YardColourBySection } from '../molecules/YardColourBySection'
import { YardViewFilterForm } from '../molecules/YardViewFilterForm'

interface Props {
  viewStore: YardViewStore
  carrierVisitAllocationRulesViewStore: CarrierVisitAllocationRulesViewStore
  globalAllocationRulesViewStore: GlobalAllocationRulesViewStore
}

export const YardViewFiltersDrawer = observer((props: Props) => {
  const { viewStore, globalAllocationRulesViewStore, carrierVisitAllocationRulesViewStore } = props
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { showUnsavedChangesDialog, attemptAction, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard()

  const onCreateRuleFromFilter = useCallback(() => {
    viewStore.setCreateRuleFromFilter(true)

    const isCarrierVisitRule =
      !!viewStore.filters.inboundCarrierVisitId || !!viewStore.filters.outboundCarrierVisitId

    const dispose = () => {
      globalAllocationRulesViewStore.resetChanges()
      carrierVisitAllocationRulesViewStore.resetChanges()
    }

    if (isCarrierVisitRule) {
      const carrierVisitId =
        viewStore.filters.inboundCarrierVisitId ?? viewStore.filters.outboundCarrierVisitId!

      const direction = viewStore.filters.inboundCarrierVisitId
        ? CarrierVisitDirection.Inbound
        : CarrierVisitDirection.Outbound

      const isMatchingContext =
        carrierVisitAllocationRulesViewStore.isAllocationRulesDrawerOpen &&
        carrierVisitAllocationRulesViewStore.selectedCarrierVisitId === carrierVisitId &&
        carrierVisitAllocationRulesViewStore.selectedCarrierVisitDirection === direction

      if (isMatchingContext) {
        carrierVisitAllocationRulesViewStore.toggleFormDialog()
        return
      }

      const hasUnsavedChanges =
        globalAllocationRulesViewStore.hasUnsavedChanges ||
        carrierVisitAllocationRulesViewStore.hasUnsavedChanges

      const action = () => {
        if (hasUnsavedChanges) {
          globalAllocationRulesViewStore.resetChanges()
          carrierVisitAllocationRulesViewStore.resetChanges()
        }
        carrierVisitAllocationRulesViewStore.openCarrierVisitRules(carrierVisitId, direction)
        carrierVisitAllocationRulesViewStore.toggleFormDialog()
        globalAllocationRulesViewStore.setAllocationRulesDrawerOpen(false)
      }

      attemptAction(hasUnsavedChanges, action, dispose)
    } else {
      const isMatchingContext = globalAllocationRulesViewStore.isAllocationRulesDrawerOpen

      if (isMatchingContext) {
        globalAllocationRulesViewStore.toggleFormDialog()
        return
      }

      const hasUnsavedChanges = carrierVisitAllocationRulesViewStore.hasUnsavedChanges
      const action = () => {
        if (hasUnsavedChanges) {
          carrierVisitAllocationRulesViewStore.resetChanges()
        }
        globalAllocationRulesViewStore.setAllocationRulesDrawerOpen(true)
        globalAllocationRulesViewStore.toggleFormDialog()
        carrierVisitAllocationRulesViewStore.setAllocationRulesDrawerOpen(false)
      }

      attemptAction(hasUnsavedChanges, action, dispose)
    }
  }, [
    carrierVisitAllocationRulesViewStore,
    globalAllocationRulesViewStore,
    viewStore,
    attemptAction,
  ])

  return (
    <>
      <Drawer
        open={viewStore.isFilterDrawerOpen && viewStore.isViewMode}
        onClose={() => {
          viewStore.setFilterDrawerOpen(false)
          viewStore.setCreateRuleFromFilter(false)
        }}
        anchor={'right'}
        variant='persistent'
      >
        <Box width={YARD_DRAWERS_WIDTH}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            borderBottom={`1px solid ${theme.palette.divider}`}
            p={theme.customSpacing.s}
            height={YARD_DRAWER_HEADER_HEIGHT}
          >
            <Typography variant='h6'>{t('filters', 'Filters')}</Typography>
            <Stack direction='row' spacing={1}>
              <Tooltip title={t('createRuleFromCurrentFilter', 'Create rule from current filters')}>
                <IconButton onClick={() => onCreateRuleFromFilter()}>
                  <PlusIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('clearFilters', 'Clear filters')}>
                <IconButton
                  onClick={() => {
                    viewStore.clearFilters()
                    viewStore.setFilterDrawerOpen(false)
                    viewStore.setCreateRuleFromFilter(false)
                  }}
                >
                  <FilterAltOff />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <YardColourBySection viewStore={viewStore} />
          <YardViewFilterForm viewStore={viewStore} />
        </Box>
      </Drawer>

      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </>
  )
})
